import { useTranslation } from 'react-i18next';
import { FormField } from '@/components/forms/FormField';
import { EntitySelector } from '@/components/ui/EntitySelector';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiEntitySelector } from '@/components/ui/MultiEntitySelector';
import { NumberOrTemplateInput } from '@/components/ui/NumberOrTemplateInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { normalizeSelectorOptions } from '@/lib/selector-options';
import { RgbColorInput } from './RgbColorInput';

interface ServiceField {
  name?: string;
  description?: string;
  example?: unknown;
  required?: boolean;
  selector?: Record<string, unknown>;
}

interface ServiceDataFieldsProps {
  serviceFields: Record<string, ServiceField>;
  currentData: Record<string, unknown>;
  onChange: (field: string, value: unknown) => void;
}

/**
 * Renders dynamic service data fields based on service definition.
 * Replaces the duplicated service field rendering logic.
 */
export function ServiceDataFields({
  serviceFields,
  currentData,
  onChange,
}: ServiceDataFieldsProps) {
  const { t } = useTranslation(['common', 'nodes']);
  if (Object.keys(serviceFields).length === 0) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-col gap-3 border-t pt-3">
      <h4 className="mb-3 font-semibold text-muted-foreground text-xs">
        {t('nodes:serviceDataFields.heading')}
      </h4>
      {Object.entries(serviceFields).map(([fieldName, field]) => {
        const selector = field.selector || {};
        const selectorType = Object.keys(selector)[0];
        const selectorConfig = selector[selectorType] || {};
        const currentValue = currentData[fieldName] as string | number | boolean | undefined;

        // Use field.name if available, otherwise format fieldName as label
        const fieldLabel =
          field.name || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

        // Render input based on selector type
        if (selectorType === 'number') {
          const config = selectorConfig as {
            unit_of_measurement?: string;
          };

          return (
            <FormField
              key={fieldName}
              label={`${fieldLabel}${config.unit_of_measurement ? ` (${config.unit_of_measurement})` : ''}`}
              required={field.required}
              description={field.description}
            >
              <NumberOrTemplateInput
                value={currentData[fieldName]}
                required={field.required}
                placeholder={field.example !== undefined ? String(field.example) : ''}
                onChange={(value) => onChange(fieldName, value ?? '')}
              />
            </FormField>
          );
        }

        if (selectorType === 'select') {
          const options = normalizeSelectorOptions(
            (selectorConfig as { options?: unknown }).options
          );

          return (
            <FormField
              key={fieldName}
              label={fieldLabel}
              required={field.required}
              description={field.description}
            >
              <Select
                value={String(currentValue === '' ? '__NONE__' : (currentValue ?? '__NONE__'))}
                onValueChange={(value) => onChange(fieldName, value === '__NONE__' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('placeholders.none')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__NONE__">{t('placeholders.none')}</SelectItem>
                  {options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          );
        }

        if (selectorType === 'boolean') {
          return (
            <div key={fieldName} className="mb-3 flex items-center gap-2">
              <input
                type="checkbox"
                checked={(currentValue as boolean) ?? false}
                onChange={(e) => onChange(fieldName, e.target.checked)}
                className="rounded"
              />
              <Label className="font-medium text-muted-foreground text-xs">
                {fieldLabel}
                {field.required && (
                  <span className="ml-0.5 text-destructive">{t('labels.requiredAsterisk')}</span>
                )}
              </Label>
            </div>
          );
        }

        if (selectorType === 'entity') {
          const config = selectorConfig as { multiple?: boolean };
          const isMultiple = config.multiple === true;

          if (isMultiple) {
            // Normalize value to array for multi-entity selector
            const arrayValue = Array.isArray(currentValue)
              ? (currentValue as string[])
              : currentValue
                ? [String(currentValue)]
                : [];

            return (
              <FormField
                key={fieldName}
                label={fieldLabel}
                required={field.required}
                description={field.description}
              >
                <MultiEntitySelector
                  value={arrayValue}
                  onChange={(value) => onChange(fieldName, value)}
                  placeholder={field.example !== undefined ? String(field.example) : undefined}
                />
              </FormField>
            );
          }

          return (
            <FormField
              key={fieldName}
              label={fieldLabel}
              required={field.required}
              description={field.description}
            >
              <EntitySelector
                value={String(currentValue ?? '')}
                onChange={(value) => onChange(fieldName, value)}
                placeholder={field.example !== undefined ? String(field.example) : undefined}
              />
            </FormField>
          );
        }

        if (selectorType === 'color_rgb' || fieldName === 'rgb_color') {
          return (
            <FormField
              key={fieldName}
              label={fieldLabel}
              required={field.required}
              description={field.description}
            >
              <RgbColorInput
                value={currentData[fieldName]}
                example={field.example}
                onChange={(value) => onChange(fieldName, value)}
              />
            </FormField>
          );
        }

        // Default: text input (for text, etc.)
        return (
          <FormField
            key={fieldName}
            label={fieldLabel}
            required={field.required}
            description={field.description}
          >
            <Input
              type="text"
              value={(currentValue as string) ?? ''}
              onChange={(e) => onChange(fieldName, e.target.value)}
              placeholder={field.example !== undefined ? String(field.example) : ''}
            />
          </FormField>
        );
      })}
    </div>
  );
}
